import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, ListItemText, ListSubheader, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import CheckIcon from '@mui/icons-material/Check';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { selectLocationLocale, setDefaultLocales } from '../../redux/slices/localeSlice';
import { countiesMapping, locationLabels, locationLocaleMapping } from '../../utils/constants';
import { ELocations } from '../../types/user';

// Resolves a friendly country name for a locale (e.g. "en-us" -> "United States").
const getCountryName = (locale: string): string => {
  const match = countiesMapping.find((country) => country.localeCode.toLowerCase() === locale.toLowerCase());
  return match?.name ?? locale.toUpperCase();
};

// Resolves the ISO region code for a locale (e.g. "en-us" -> "US").
const getRegionCode = (locale: string): string => {
  try {
    return new Intl.Locale(locale.toLowerCase()).region ?? locale.toUpperCase();
  } catch {
    return locale.toUpperCase();
  }
};

const MarketSelector = () => {
  const dispatch = useAppDispatch();
  const locationPermission = useAppSelector((state) => state.user.permissions?.locationPermission);
  const selectedLocales = useAppSelector((state) => state.locale.selectedLocales);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Locations the user is permitted to see, intersected with the locations we map locales for.
  const availableLocations = useMemo<ELocations[]>(() => {
    const allLocations = Object.keys(locationLocaleMapping) as ELocations[];
    if (!locationPermission || locationPermission.length === 0) {
      return [];
    }
    return allLocations.filter((location) => locationPermission.includes(location));
  }, [locationPermission]);

  // Default selection: North America auto-selects all of its locales, every
  // other permitted location defaults to its first locale (switchable).
  useEffect(() => {
    if (availableLocations.length === 0) {
      return;
    }
    const defaults = availableLocations.flatMap((location) =>
      location === ELocations.NORTH_AMERICA ? locationLocaleMapping[location] : [locationLocaleMapping[location][0]]
    );
    dispatch(setDefaultLocales(defaults.filter(Boolean)));
  }, [availableLocations, dispatch]);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelectLocale = useCallback(
    (locale: string, location: ELocations) => {
      dispatch(selectLocationLocale({ locale, locationLocales: locationLocaleMapping[location] }));
    },
    [dispatch]
  );

  if (availableLocations.length === 0) {
    return null;
  }

  // Tooltip explaining the purpose of the selector: choosing markets controls
  // which locale's pricing and bundle values are shown in the grid.
  const tooltipTitle = 'Set pricing & bundles by market';
  const menuDescription = 'Set pricing & bundles by below regions';

  // North America shows all of its locales together on one auto-selected,
  // non-switchable line. Every other location renders switchable locales.
  const renderLocationItems = (location: ELocations) => {
    if (location === ELocations.NORTH_AMERICA) {
      return (
        <MenuItem
          key={`__locales-${location}`}
          dense
          disableRipple
          sx={{ pl: 3, minWidth: 240, cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}
        >
          <ListItemText
            primary={
              <Box
                component="span"
                sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}
              >
                {locationLocaleMapping[location].map((locale, index) => (
                  <Box component="span" key={locale} sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                    {index > 0 && (
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>
                        ·
                      </Typography>
                    )}
                    <Typography component="span" variant="body2">
                      {getCountryName(locale)}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary">
                      {getRegionCode(locale)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            }
          />
          <Tooltip title="Auto-selected" arrow>
            <CheckIcon fontSize="small" color="primary" sx={{ ml: 2 }} />
          </Tooltip>
        </MenuItem>
      );
    }

    return locationLocaleMapping[location].map((locale) => {
      const isSelected = selectedLocales.includes(locale);
      return (
        <MenuItem
          key={locale}
          dense
          selected={isSelected}
          onClick={() => handleSelectLocale(locale, location)}
          sx={{ pl: 3, minWidth: 220 }}
        >
          <ListItemText
            primary={
              <Box component="span" sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography component="span" variant="body2">
                  {getCountryName(locale)}
                </Typography>
                <Typography component="span" variant="caption" color="text.secondary">
                  {getRegionCode(locale)}
                </Typography>
              </Box>
            }
          />
          {isSelected && <CheckIcon fontSize="small" color="primary" sx={{ ml: 2 }} />}
        </MenuItem>
      );
    });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={tooltipTitle} arrow>
        <IconButton
          onClick={handleOpen}
          sx={{
            color: '#fff',
            borderRadius: 1,
            gap: 0.5,
            '&:hover': { backgroundColor: '#333' },
          }}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          aria-label="Select markets"
        >
          <Icon icon="mdi:earth" style={{ fontSize: '22px' }} />
          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1 }}>
            Regions
          </Typography>
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} MenuListProps={{ sx: { py: 0 } }}>
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {menuDescription}
          </Typography>
        </Box>
        {availableLocations.flatMap((location) => [
          <ListSubheader
            key={`__header-${location}`}
            sx={{
              lineHeight: '34px',
              fontWeight: 700,
              fontSize: 13,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.primary',
              backgroundColor: 'grey.200',
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {locationLabels[location] ?? location}
          </ListSubheader>,
          renderLocationItems(location),
        ])}
      </Menu>
    </Box>
  );
};

export default MarketSelector;
